import {
  Controller,
  FieldPath,
  FieldPathValue,
  FieldValues,
} from 'react-hook-form';
import { InputNumber, InputNumberProps } from 'antd';
import { useAdditionalFormContext } from '../AdditionalFormContext';
import { useGetIdFromName } from '../utils';
import { parseNumber } from '../../utils/parsers/parseNumber';
import { isOk } from '../../utils/parsers/result';

export interface FormInputNumberProps<
  FormData extends FieldValues,
  Name extends FieldPath<FormData>,
  Value extends FieldPathValue<FormData, Name> = FieldPathValue<FormData, Name>,
> extends Omit<InputNumberProps<Value>, 'id'> {
  id?: string;
  name: Name;
  stringFormat?: boolean;
}

export const FormInputNumber = <
  FormData extends FieldValues,
  Name extends FieldPath<FormData>,
>(
  props: FormInputNumberProps<FormData, Name>,
) => {
  const getIdFromName = useGetIdFromName();
  const { stringFormat, name, id = getIdFromName(name), ...rest } = props;
  const { onChange: formOnChange } = useAdditionalFormContext();

  return (
    <Controller
      name={name}
      render={({ field }) => {
        const { value } = field;
        const onChange = (value: any) => {
          field.onChange({
            target: { value: stringFormat ? value?.toString() : value },
          });
          formOnChange?.();
        };
        return (
          <InputNumber
            {...rest}
            disabled={props.disabled}
            formatter={numberFormatter}
            id={id}
            stringMode={stringFormat}
            value={value}
            onChange={onChange}
          />
        );
      }}
    />
  );
};

function numberFormatter(value: string | undefined) {
  if (!value) {
    return value ?? '';
  }

  const parsed = parseNumber(value);
  if (isOk(parsed)) {
    return value.replaceAll(/ /gi, '');
  }

  return value;
}
